<template>
  <div class="w-full">
    <div v-if="loading" class="mt-6 flex justify-center">
      <LoadingSpinner/>
    </div>

    <div v-else-if="!ranked.length" class="mt-10 text-center text-gray-400 text-sm">
      {{ emptyText }}
    </div>

    <div v-else>
      <Transition appear name="banner">
        <div
          v-if="me"
          class="mb-3 rounded-lg bg-gradient-to-r from-green-400/20 to-teal-400/20 ring-1 ring-green-400/30 py-2 px-3 text-center text-sm">
          {{ rankEmoji }} You're <span class="text-gradient font-extrabold">{{ ordinal(me.rank) }}</span><span v-if="periodLabel">&nbsp;{{ periodLabel }}</span>!
        </div>
      </Transition>

      <div class="flex flex-col">
        <div
          v-for="(row, index) in ranked"
          :key="row.id"
          class="flex items-center gap-2 py-2 px-2 rounded-lg"
          :class="rowClass(row, index)">
          <!-- rank: gold/silver/bronze trophy for the top 3, plain number otherwise -->
          <div class="w-7 shrink-0 flex justify-center">
            <font-awesome-icon v-if="row.rank <= 3" :icon="['fas', 'trophy']" :class="medal[row.rank]"/>
            <span v-else class="text-sm text-gray-400">{{ row.rank }}</span>
          </div>
          <div class="flex items-center gap-1.5 min-w-0 flex-1">
            <span class="truncate text-sm" :class="{ 'text-green-300 font-semibold': isMe(row) }">{{ row.name }}</span>
            <font-awesome-icon v-if="row.isPerfect" :icon="['fas', 'fire']" class="text-orange-400 text-xs shrink-0"/>
            <img v-if="row.verified" src="/favicon-16x16.png" title="Syndles Member" class="w-3.5 h-3.5 shrink-0"/>
            <span v-if="isMe(row)" class="text-[10px] bg-green-400 text-gray-900 font-bold rounded px-1 shrink-0">You</span>
          </div>
          <span v-if="row.chip" class="text-[10px] text-gray-300 bg-gray-700 rounded px-1.5 py-0.5 shrink-0">{{ row.chip }}</span>
          <span class="font-bold text-sm w-12 text-right shrink-0">{{ row.valueDisplay }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed} from "vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

const props = defineProps({
  // Each entry: { id, name, verified, valueDisplay, isPerfect?, chip? }
  entries: {type: Array, default: () => []},
  loading: {type: Boolean, default: false},
  emptyText: {type: String, default: "Nothing here yet."},
  currentUserId: {type: String, default: ""},
  // e.g. "today" / "of all time" — used in the "You're 6th today!" banner.
  periodLabel: {type: String, default: ""}
});

// Medal colour for the top-3 trophy icons (literal strings for Tailwind's scan).
const medal = {
  1: 'text-gold-500',
  2: 'text-silver-500',
  3: 'text-bronze-500'
};

const ranked = computed(() => props.entries.map((e, i) => ({...e, rank: i + 1})));
const me = computed(() => ranked.value.find(e => e.id === props.currentUserId));

const rankEmoji = computed(() => {
  if (!me.value) {
    return '';
  }
  if (me.value.rank === 1) {
    return '👑';
  }
  if (me.value.rank <= 3) {
    return '🔥';
  }
  return '🎯';
});

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function isMe(row) {
  return !!props.currentUserId && row.id === props.currentUserId;
}

function rowClass(row, index) {
  if (isMe(row)) {
    return 'bg-green-400/10 ring-1 ring-green-400/40';
  }
  return index % 2 === 1 ? 'bg-gray-800/40' : '';
}
</script>

<style scoped>
.banner-enter-active {
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.banner-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.97);
}

.banner-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
